import React, { useEffect, useState } from 'react';
import { Button, Card, Collapse, Flex } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import { selectedIndicesState } from "../ThreeJs/ReactStates/states";
import { useRecoilState } from "recoil";
import { DrawingModes, ObstacleTypes, POLYGONS } from "../ThreeJs/Global/variables";
import { cleanUp, removeLineMeshes } from "../ThreeJs/Global/functions";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import { MdCheckBoxOutlineBlank, MdOutlineCircle } from "react-icons/md";
import { themeState } from "../lib/atom";
import { deselectSegment } from "../ThreeJs/Segments/Functions";
import { deselectObstacle } from "../ThreeJs/Obstacles/ObstacleFunctions";
import { deselectPanels } from '../ThreeJs/Panels/PanelFunctions';
import { Tooltip } from "antd";
import { flushObstacleToScene } from "../ThreeJs/Obstacles/ObstacleManager";
import { MdDone } from "react-icons/md"; // or any flush icon you prefer

const SegmentNavigator = ({ items, onChange, title, updateDrawingMode, currentDrawingMode, updateObstacleType,
    setShowObsTab, showObsTab, setShowPanelSettings, showPanelSettings }) => {

    // Icons to represent the available obstruction types
    const TabIcons = [MdCheckBoxOutlineBlank, MdOutlineCircle];

    // State variables
    const [activeObsTab, setActiveObsTab] = useState(null); // Active obstruction tab
    const [selectedIndices, setSelectedIndices] = useRecoilState(selectedIndicesState); // Selected indices from recoil state
    const [buttonText, setButtonText] = useState(""); // Text to display on the button
    const [buttonType, setButtonType] = useState({}); // Type of button (primary or danger)
    const [buttonStyle, setButtonStyle] = useState({}); // Style of the button
    const theme = useRecoilState(themeState)[0]; // Theme from recoil state (dark or light mode)
    const [hoveredIndex, setHoveredIndex] = useState(null); // Track which obstruction icon is being hovered

    // Effect to update button text and style based on the current drawing mode
    useEffect(() => {
        if (currentDrawingMode === null || currentDrawingMode === DrawingModes.NONE) {
            setButtonText('Add Segment');
            setButtonType('primary');
            setButtonStyle('primary');
        } else if (currentDrawingMode === DrawingModes.SEGMENT) {
            setButtonText('Cancel Segment');
            setButtonType('primary');
            setButtonStyle('danger');
            deselect(); // Deselect previously selected objects when starting a new segment
        } else if (currentDrawingMode === DrawingModes.PANEL) {
            setButtonText('Add Segment');
            setButtonType('primary');
            setButtonStyle('disabled'); // Disable the button while in PANEL mode
        }
    }, [currentDrawingMode]);

    // Function to deselect selected objects (segments, obstacles, panels)
    function deselect() {
        let selectedSegment = selectedIndices.segmentIndex;
        let selectedObstacle = selectedIndices.obstacleIndex;
        let selectedPanels = selectedIndices.panelIndex;

        // Deselect each type of selected object if present
        if (selectedSegment != null) {
            deselectSegment(selectedSegment);
        }
        if (selectedObstacle !== null) {
            let selectedObs = POLYGONS[selectedSegment].obstructions[selectedIndices.obstacleIndex];
            deselectObstacle(selectedObs);
        }
        if (selectedPanels !== null) {
            deselectPanels(selectedPanels, setSelectedIndices, selectedIndices);
            setShowPanelSettings(false);
        }

        // Clear selected indices
        setSelectedIndices({ segmentIndex: null, obstacleIndex: null, panelIndex: null });
    }

    // Function to handle the click on an obstruction tab (rectangle, circle, or triangle)
    const handleObsTabClick = (index) => {
        const shapes = [ObstacleTypes.RECTANGLE, ObstacleTypes.CIRCLE, "triangle"];
        const clickedShape = shapes[index];

        // Set the selected obstruction type and update drawing mode to OBSTACLE
        setShowObsTab(false);
        setActiveObsTab(index);
        updateObstacleType(clickedShape);
        updateDrawingMode(DrawingModes.OBSTACLE);
        setShowPanelSettings(false);
    };

    // Function to handle adding or canceling a segment based on current drawing mode
    const handleAddSegmentClick = () => {
        if (currentDrawingMode === null || currentDrawingMode === DrawingModes.NONE || currentDrawingMode === DrawingModes.PANEL) {
            // Start drawing a segment
            updateDrawingMode(DrawingModes.SEGMENT);
            setShowPanelSettings(false);
        } else if (currentDrawingMode === DrawingModes.SEGMENT) {
            // Cancel drawing the segment
            updateDrawingMode(DrawingModes.NONE);
            removeLineMeshes(sceneManager.scene);
            cleanUp();
        }
    };

    // Function to cancel the obstruction tab selection
    const handleCancelObsClick = () => {
        updateDrawingMode(DrawingModes.NONE);
        setShowObsTab(false);
    };

    // Effect to reset the obstruction tab when drawing mode is NONE
    useEffect(() => {
        if (currentDrawingMode === DrawingModes.NONE) {
            setActiveObsTab(null);
        }
    }, [currentDrawingMode]);

    return (
        <Card
            //   title={title}
            bodyStyle={{ padding: "0" }}
            className='segment__card'
        >
            {items.length > 0 && showObsTab === false && activeObsTab == null && currentDrawingMode !== DrawingModes.SEGMENT ? (
                <Flex vertical style={{ height: '150px', overflow: 'auto' }}
                    className={` ${theme ? 'custom-scrollbar-dark' : 'custom-scrollbar'}`}>
                    <Collapse
                        accordion
                        items={items}
                        activeKey={selectedIndices.segmentIndex}
                        expandIcon={({ isActive }) => (
                            <CaretRightOutlined style={{ color: '#ccc' }} rotate={isActive ? 90 : 0} />
                        )}
                        ghost
                        defaultActiveKey={['1']}
                        onChange={onChange}
                    />
                </Flex>
            ) : currentDrawingMode === DrawingModes.SEGMENT ? (
                <div style={{ textAlign: 'center', fontSize: '16px', margin: '40px' }} className={`${!theme ? 'text-black' : 'text-light'}`}>
                    Draw Segment On Screen
                </div>
            ) : showObsTab === true && activeObsTab == null ? (
                <div style={{ margin: '5px 0' }}>
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>Pick an obstruction</div>
                    <Flex horizontal style={{ justifyContent: 'center' }}>
                        {TabIcons.map((Icon, index) => (
                            <Card
                                key={index}
                                style={{
                                    width: '45px',
                                    height: '45px',
                                    margin: '0 9px',
                                    borderRadius: '12px',
                                    background: hoveredIndex === index ? (theme ? '#007bff' : '#007bff') : (theme ? '#CBCBCB' : '#CBCBCB'),
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    paddingTop: '5px',
                                    boxShadow: hoveredIndex === index ? '0px 4px 8px rgba(0, 0, 0, 0.1)' : 'none'
                                }}
                                onClick={() => handleObsTabClick(index)}
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            >
                                <Icon
                                    size={30}
                                    color={index === hoveredIndex ? '#fff' : (theme ? '#676767' : '#676767')}
                                />
                            </Card>
                        ))}
                    </Flex>
                </div>
            ) : activeObsTab != null ? (
                <div style={{ textAlign: 'center', fontSize: "16px", margin: "40px" }} className={`${!theme ? 'text-black' : 'text-light'}`}>
                    Start Drawing
                </div>
            ) : (
                <div style={{ textAlign: 'center', fontSize: "16px", margin: "40px" }} className={`${!theme ? 'text-black' : 'text-light'}`}>
                    No Segments Found
                </div>
            )}

            {/* Display buttons based on the state */}
            {showObsTab === false && activeObsTab == null ? (
                <div style={{ marginTop: '5px' }}>
                    <Button block type={buttonType} danger={buttonStyle === 'danger'} disabled={buttonStyle === 'disabled'} onClick={handleAddSegmentClick}>
                        {buttonText}
                    </Button>
                </div>
            ) : (
                <div style={{ marginTop: '5px' }}>
                    <Button block type="primary" danger onClick={handleCancelObsClick}>
                        Cancel
                    </Button>
                </div>
            )}
        </Card>
    );
}

export default SegmentNavigator;
